/**
 * One switch that will not flip until the person flipping it has been told what
 * it does.
 *
 * Used three times on the Integrations screen — open tracking, click tracking,
 * and Virtual Deliverability Manager — and written once because the rule it
 * encodes is the same rule in all three cases and must not be able to drift
 * into two of them.
 *
 * ## Why this is not a toggle
 *
 * `@wend/ui` has a Checkbox and no Switch, and the obvious thing was to reach
 * for the Checkbox and let a click write straight through to the API. That was
 * rejected, twice over.
 *
 * The first reason is that an instant toggle **shows the disclosure after the
 * decision**. A tenant mailing cooperative banks may have told their members
 * that Wend does not track them; the sentence that tells them a pixel is about
 * to be added to every message is worth nothing if it appears in a toast once
 * the pixel is already on. So the disclosure is on screen *before* the control,
 * always, in full, and the control's own label names the effect.
 *
 * The second is that a checkbox that flips visually and then waits for a server
 * round trip is lying for the duration of the round trip, and lying permanently
 * if the write is refused. `evaluateTrackingChange` refuses these changes for
 * five different reasons; a control whose resting state is "whatever you last
 * clicked" would show tracking as on to somebody whose write the API had just
 * turned down.
 *
 * ## The confirmation is asymmetric, and that is deliberate
 *
 * Turning something **on** takes two presses: the button, then a confirmation
 * that restates the disclosure and whose own label says what will happen —
 * "Yes, add a tracking pixel to my messages", not "Confirm". Turning something
 * **off** takes one, and happens immediately.
 *
 * That asymmetry is not an inconsistency to be tidied away. The risk is
 * asymmetric: switching tracking on changes what happens to other people's mail
 * and may break a promise the tenant made to somebody else, while switching it
 * off is the state this product ships in and the state it defends. A
 * confirmation dialog in front of "stop tracking my recipients" would be
 * friction placed in exactly the wrong direction — and the same argument is why
 * the off button is never `loading`-locked behind a second thought.
 *
 * This is the shape `DisconnectPanel` on the same screen already uses for the
 * one other decision here with a consequence: the confirmation's job is not to
 * ask "are you sure" but to say **what changes**.
 *
 * ## The refusal
 *
 * When `refusal` is set, the on-button is not offered at all — not offered and
 * disabled, not offered and failing. A disabled control with a tooltip is a
 * control that tells you it is unavailable and not why. The refusal's sentence
 * comes from `TRACKING_CHANGE_REFUSAL_MESSAGES` in `@wend/shared`, so it is the
 * same sentence the API would log if the write were attempted anyway.
 *
 * A refusal never hides the disclosure. Somebody deciding whether the work of
 * verifying a subdomain is worth doing needs to read what they would be turning
 * on before they decide to go and do it.
 */
import { Badge, Button, Callout } from '@wend/ui';
import { useState, type ReactNode } from 'react';

import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';

export interface DisclosedSwitchProps {
  /** The mechanism's name. "Open tracking", "Click tracking". */
  readonly title: string;
  /** Whether it is on right now, as the server last described it. */
  readonly on: boolean;
  /**
   * What it does to a recipient's mail, in plain words. Rendered verbatim and
   * always visible — never truncated, never behind a "learn more", never in a
   * tooltip. On this screen every one of these arrives on the wire from
   * `TRACKING_DISCLOSURE` so that the API's audit entry and this paragraph are
   * provably the same sentence.
   */
  readonly disclosure: string;
  /**
   * The on-button's label, which must name the effect rather than the control.
   * "Turn on open tracking", not "Enable".
   */
  readonly turnOnLabel: string;
  /** The confirmation button's label. Names what happens to recipients. */
  readonly confirmLabel: string;
  /**
   * What the confirmation says above its buttons: the scope of the change, in
   * this switch's own terms.
   *
   * Required, and not defaulted to a generic sentence. The three switches on
   * this screen change genuinely different things — two change what is inside a
   * recipient's mail, the third changes what Amazon charges — and one shared
   * "this changes every message you send" would be false on the third and
   * would be false quietly, which is the kind of copy bug that survives review
   * because it reads fine.
   */
  readonly confirmNote: ReactNode;
  /** The off-button's label. */
  readonly turnOffLabel: string;
  /**
   * Why turning it on is refused, or null. When set, no on-button is rendered
   * and this is shown in its place.
   */
  readonly refusal: string | null;
  /**
   * Extra content inside the confirmation, above its buttons — VDM's cost
   * acknowledgement lives here. Rendered only while confirming, because it is
   * consent to a specific act and not standing background information.
   */
  readonly confirmExtra?: ReactNode;
  /** Blocks the confirmation button. VDM uses it for the unticked acknowledgement. */
  readonly confirmDisabled?: boolean;
  /** Called when the confirmation is dismissed, so an owner can reset `confirmExtra`. */
  readonly onCancelConfirm?: () => void;
  /** Performs the write. Rejecting shows the failure and leaves the switch alone. */
  readonly onChange: (next: boolean) => Promise<void>;
  /** True while some *other* control on the card is writing. */
  readonly busy?: boolean;
}

export function DisclosedSwitch({
  title,
  on,
  disclosure,
  turnOnLabel,
  confirmLabel,
  confirmNote,
  turnOffLabel,
  refusal,
  confirmExtra,
  confirmDisabled = false,
  onCancelConfirm,
  onChange,
  busy = false,
}: DisclosedSwitchProps) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<RequestFailure | null>(null);

  async function apply(next: boolean): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      await onChange(next);
      // Only on success. A refused change leaves the confirmation open with its
      // reason above it, so the person can read what happened without having to
      // find their way back to the same button.
      setConfirming(false);
    } catch (error) {
      setFailure(requestFailure(error));
    } finally {
      setPending(false);
    }
  }

  function cancel(): void {
    setConfirming(false);
    setFailure(null);
    onCancelConfirm?.();
  }

  return (
    <section className="flex flex-col gap-3 border-t border-line py-5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-[0.98rem] font-medium text-text">{title}</h3>
        {/*
          The state in a word as well as a colour, and the word is the same one
          the button offers to change it to — so "On" beside "Turn off open
          tracking" reads as one fact rather than two.
        */}
        <Badge tone={on ? 'info' : 'neutral'}>{on ? 'On' : 'Off'}</Badge>
      </div>

      {/* Verbatim, always, whether the switch is on, off or refused. */}
      <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">{disclosure}</p>

      {failure !== null && <RequestFailureAlert failure={failure} />}

      {on ? (
        <div className="flex flex-wrap items-center gap-3">
          {/*
            One press, no confirmation. Stopping is the safe direction and the
            product's default; putting a second step in front of it would be
            friction pointed at the wrong outcome.
          */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={pending}
            disabled={busy}
            onClick={() => {
              void apply(false);
            }}
          >
            {turnOffLabel}
          </Button>
        </div>
      ) : refusal !== null ? (
        // No button at all. A disabled control tells somebody it is unavailable
        // and not why; this tells them why and, on the panels below, what to do.
        <Callout tone="warn" title="This cannot be switched on yet">
          {refusal}
        </Callout>
      ) : confirming ? (
        <div className="flex flex-col gap-3 rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
          <div className="max-w-prose text-[0.92rem] leading-relaxed text-text">{confirmNote}</div>

          {confirmExtra}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              loading={pending}
              // `busy` as well as `confirmDisabled`: a confirmation left open
              // while another control on the card writes would otherwise fire a
              // second whole-body PUT into the first one's round trip, and the
              // two would race over the same three booleans.
              disabled={confirmDisabled || busy}
              onClick={() => {
                void apply(true);
              }}
            >
              {confirmLabel}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={pending} onClick={cancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => {
              setFailure(null);
              setConfirming(true);
            }}
          >
            {turnOnLabel}
          </Button>
        </div>
      )}
    </section>
  );
}
